// src/hooks/useVarLabelFormatter.js
import { useMemo, useCallback } from 'react';

/**
 * Returns a formatter that decorates column headers with variable labels when available.
 * Mirrors the behaviour used on graph dashboards so tables can share the same UX.
 */
export function useVarLabelFormatter(metaSrc, showVarLabels) {
  const { headerMap, groupLabelMap } = useMemo(() => {
    const hMap = new Map();
    const gMap = new Map();

    const registerMeta = (key, col) => {
      if (key == null) return;
      const str = String(key);
      if (!str) return;
      if (!hMap.has(str)) {
        hMap.set(str, col);
      }
    };

    const registerGroup = (key, label) => {
      if (key == null || typeof label !== 'string') return;
      const strKey = String(key);
      const strLabel = label.trim();
      if (!strKey || !strLabel) return;
      if (!gMap.has(strKey)) {
        gMap.set(strKey, strLabel);
      }
    };

    (Array.isArray(metaSrc) ? metaSrc : []).forEach((col) => {
      if (!col) return;

      registerMeta(col.header, col);
      registerMeta(col.id, col);
      if (Array.isArray(col.aliases)) {
        col.aliases.forEach((alias) => registerMeta(alias, col));
      }

      if (col.groupname) {
        registerGroup(col.groupname, col.groupLabel);
      }
      // Some payloads store group labels directly on header/id for convenience
      if (col.header) {
        registerGroup(col.header, col.groupLabel);
      }
      if (col.id) {
        registerGroup(col.id, col.groupLabel);
      }
    });

    return { headerMap: hMap, groupLabelMap: gMap };
  }, [metaSrc]);

  return useCallback((hdr) => {
    const raw = String(hdr ?? '');
    const key = raw;
    const meta = headerMap.get(key);

    if (showVarLabels) {
      if (meta?.varLabel) {
        return `${meta.varLabel} (${key})`;
      }

      const mg = raw.match(/^\s*([^•]+?)\s*•\s*(r\d+)(?:_ağırlıklı_[^\s]+)?\s*$/i);
      if (mg && mg[1] && mg[2]) {
        const gname = mg[1].trim();
        const rowTok = mg[2].trim();
        const candidate = `${gname}_${rowTok}_c1`;
        const m2 = headerMap.get(candidate);
        if (m2?.varLabel) return `${m2.varLabel} (${key})`;
        if (typeof m2?.groupLabel === 'string' && m2.groupLabel.trim()) {
          return `${m2.groupLabel.trim()} (${key})`;
        }
      }

      const gLabel = groupLabelMap.get(key);
      if (gLabel) return `${gLabel} (${key})`;
    }

    return key;
  }, [headerMap, groupLabelMap, showVarLabels]);
}
